import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CalendarDays, UserX, Loader2 } from "lucide-react";
import { format, differenceInCalendarDays, parseISO, isValid } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REASON_COLORS = {
  "Annual Leave": "bg-[#DBEAFE] text-[#1D4ED8] border-[#BFDBFE]",
  "Medical Leave": "bg-[#FEE2E2] text-[#DC2626] border-[#FECACA]",
  "Emergency Leave": "bg-[#FEF3C7] text-[#B45309] border-[#FDE68A]",
  "Training": "bg-[#EDE9FE] text-[#6D28D9] border-[#DDD6FE]",
  "Public Holiday": "bg-[#D1FAE5] text-[#047857] border-[#A7F3D0]",
  "Other": "bg-[#F4F4F5] text-[#52525B] border-[#E4E4E7]",
};

const AbsenceWidget = ({ onOpenCalendar }) => {
  const [engineers, setEngineers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/engineer-directory`);
        if (!cancelled) setEngineers(res.data || []);
      } catch (e) {
        // silent - widget is non-blocking
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const upcoming = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const horizon = new Date(today);
    horizon.setDate(horizon.getDate() + 6); // inclusive 7-day window (today + next 6)
    horizon.setHours(23, 59, 59, 999);

    const rows = [];
    for (const eng of engineers) {
      const s = eng.absence_start_date;
      const e = eng.absence_end_date;
      if (!s || !e) continue;
      const start = parseISO(s);
      const end = parseISO(e);
      if (!isValid(start) || !isValid(end)) continue;
      // overlaps [today, horizon]?
      if (end < today || start > horizon) continue;
      rows.push({ ...eng, _start: start, _end: end });
    }
    rows.sort((a, b) => a._start - b._start);
    return rows;
  }, [engineers]);

  const formatWindow = (row) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startsInDays = differenceInCalendarDays(row._start, today);
    const endsInDays = differenceInCalendarDays(row._end, today);
    if (startsInDays <= 0 && endsInDays >= 0) {
      if (endsInDays === 0) return "Back tomorrow";
      return `Out today · back in ${endsInDays + 1}d`;
    }
    if (startsInDays === 1) return "Starts tomorrow";
    return `Starts in ${startsInDays}d`;
  };

  if (loading) {
    return (
      <div
        className="bg-white border border-[#E4E4E7] rounded-sm p-4 mb-6 flex items-center gap-2 text-sm text-[#71717A]"
        data-testid="absence-widget-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading upcoming absences...
      </div>
    );
  }

  if (upcoming.length === 0) {
    return null; // hide widget entirely when nobody is out
  }

  return (
    <div
      className="bg-white border border-[#E4E4E7] rounded-sm p-4 mb-6"
      data-testid="absence-widget"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-sm bg-[#FEF3C7] flex items-center justify-center">
            <UserX className="w-4 h-4 text-[#B45309]" />
          </div>
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#B45309]">
              Coverage Gaps
            </div>
            <div className="text-sm font-semibold text-[#09090B]">
              <span data-testid="absence-widget-count">{upcoming.length}</span>{" "}
              engineer{upcoming.length > 1 ? "s" : ""} out in the next 7 days
            </div>
          </div>
        </div>
        {onOpenCalendar && (
          <button
            type="button"
            onClick={onOpenCalendar}
            className="text-xs font-semibold text-[#0F172A] hover:underline flex items-center gap-1"
            data-testid="absence-widget-open-calendar"
          >
            <CalendarDays className="w-3.5 h-3.5" />
            Full calendar
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {upcoming.slice(0, 12).map((row) => {
          const reason = row.absence_reason || "Other";
          const color = REASON_COLORS[reason] || REASON_COLORS.Other;
          return (
            <div
              key={row.id}
              className={`inline-flex flex-col px-3 py-1.5 rounded-sm border ${color}`}
              data-testid={`absence-widget-row-${row.id}`}
              title={`${row.name} · ${reason} · ${format(row._start, "MMM d")} → ${format(row._end, "MMM d")}`}
            >
              <span className="text-xs font-semibold leading-tight">{row.name}</span>
              <span className="text-[10px] font-medium leading-tight opacity-80">
                {reason} · {formatWindow(row)}
              </span>
            </div>
          );
        })}
        {upcoming.length > 12 && (
          <div className="inline-flex items-center px-3 py-1.5 rounded-sm bg-[#F4F4F5] border border-[#E4E4E7] text-xs font-semibold text-[#52525B]">
            +{upcoming.length - 12} more
          </div>
        )}
      </div>
    </div>
  );
};

export default AbsenceWidget;
